// ------------------------------------------------------------
// Producery – Wiki Pages (Cloudscape + React Router v6)
// ------------------------------------------------------------
// WikiList.tsx
// ------------------------------------------------------------
// Description: Displays a list of all top level wiki pages and a summary.
// - Recently edited pages
// - Pinned pages
// - Search bar to filter pages by title or tags
// - Button to create a new wiki page
// - Clicking a page navigates to its detailed view
// ------------------------------------------------------------
// Copyright (c) 2025 The Horrible Meadery
// Licensed under the MIT License
// ------------------------------------------------------------

import {
  Alert,
  Box,
  Button,
  ContentLayout,
  ExpandableSection,
  Header,
  Input,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { WikiPage } from '../../models/Wiki';
import { buildWikiTree, listPinned, listSubpages, listTopLevelPages, WikiTreeNode } from './Wiki.logic';
import { TreeNode, useSession } from './Wiki.shared';

const WikiList = () => {
  const { tenantId } = useSession();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [pinned, setPinned] = useState<WikiPage[]>([]);
  const [top, setTop] = useState<WikiPage[]>([]);
  const [all, setAll] = useState<WikiPage[]>([]);

  const [q, setQ] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const [p, t] = await Promise.all([listPinned(tenantId), listTopLevelPages(tenantId)]);

        // also fetch first-level children (optional; good enough for v1 tree)
        const children = (await Promise.all(t.map((tp) => listSubpages(tenantId, tp.slug)))).flat();

        if (!mounted) return;
        setPinned(p);
        setTop(t);
        setAll([...t, ...children]);
      } catch (e: any) {
        if (!mounted) return;
        setErr(e.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [tenantId]);

  const tree: WikiTreeNode[] = useMemo(() => buildWikiTree(all), [all]);

  const filteredTree = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return tree;

    function filterNode(node: WikiTreeNode): WikiTreeNode | null {
      const hit =
        node.title.toLowerCase().includes(term) ||
        node.slug.toLowerCase().includes(term) ||
        (node.tags || []).some((t) => t.toLowerCase().includes(term)) ||
        (node.categories || []).some((c) => c.toLowerCase().includes(term));
      const kids = (node.children || []).map(filterNode).filter(Boolean) as WikiTreeNode[];
      if (hit || kids.length) return { ...node, children: kids };
      return null;
    }

    return tree.map(filterNode).filter(Boolean) as WikiTreeNode[];
  }, [tree, q]);

  return (
    <ContentLayout
      disableOverlap
      header={
        <Header
          variant="h1"
          actions={
            <Button variant="primary" onClick={() => navigate('/wiki/new')}>
              New page
            </Button>
          }
        >
          Wiki
        </Header>
      }
    >
      <SpaceBetween size="l">
        {err && (
          <Alert type="error" header="Error loading wiki">
            {err}
          </Alert>
        )}

        <Input value={q} onChange={(e) => setQ(e.detail.value)} placeholder="Search title, slug, tags, categories…" />

        {loading ? (
          <Box>
            <Spinner /> Loading…
          </Box>
        ) : (
          <SpaceBetween size="l">
            {pinned.length > 0 && (
              <ExpandableSection headerText={`Pinned (${pinned.length})`}>
                <SpaceBetween size="xs">
                  {pinned.map((p) => (
                    <div key={p.slug}>
                      <Link to={`/wiki/${p.slug}`}>{p.title}</Link>
                      {p.status !== 'published' && (
                        <Box margin={{ left: 'xs' }} variant="code" color="text-status-inactive">
                          {p.status}
                        </Box>
                      )}
                    </div>
                  ))}
                </SpaceBetween>
              </ExpandableSection>
            )}

            <ExpandableSection headerText="Pages" defaultExpanded>
              {filteredTree.length === 0 ? (
                <Box color="text-body-secondary">No pages yet.</Box>
              ) : (
                <Box>
                  {filteredTree.map((n) => (
                    <TreeNode key={n.slug} node={n} />
                  ))}
                </Box>
              )}
            </ExpandableSection>

            {top.length === 0 && pinned.length === 0 && (
              <Box color="text-body-secondary">Start by creating your first page.</Box>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default WikiList;
